import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/app";
import { useAuth } from "@/lib/auth";
import TidoEnvBadge from "@/components/TidoEnvBadge";
import { CloudArrowDown, CheckCircle, Warning, ArrowClockwise, Buildings } from "@phosphor-icons/react";

const MESIACE = ["", "Január", "Február", "Marec", "Apríl", "Máj", "Jún",
                 "Júl", "August", "September", "Október", "November", "December"];

const HINTS = [
  "Sťahujem hodinové realita dáta…",
  "Sťahujem plánované obsadenie…",
  "Sťahujem otváracie hodiny (shift-times)…",
  "Analyzujem hodinový výkon (hourly_performance)…",
  "Ukladám do lokálnej databázy…",
  "Overujem konzistentnosť naprieč mesiacmi…",
];

/** Safely stringify any error detail (string, array-of-objects from 422, or raw). */
function errToString(e) {
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((x) => x?.msg || JSON.stringify(x)).join("; ");
  if (detail) { try { return JSON.stringify(detail); } catch { return String(detail); } }
  return e?.message || "Sťahovanie zlyhalo";
}

export default function TidoUvitanie() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setLockedStoreId } = useAuth();
  const [status, setStatus] = useState("running"); // running | done | error
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [hintIdx, setHintIdx] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [progress, setProgress] = useState({ current: 0, total: 0, currentMonth: null }); // { current, total, currentMonth: {year, month} }
  const startedRef = useRef(false);

  const idp = params.get("IDP") || params.get("idp");
  const rok = params.get("ROK") || params.get("rok") || new Date().getFullYear();
  const mesiac = params.get("MESIAC") || params.get("mesiac") || (new Date().getMonth() + 1);
  const forceAll = params.get("force_all") === "1" || params.get("FORCE_ALL") === "1";
  const domena = params.get("domena") || params.get("DOMENA") || null;

  // Rotate hints
  useEffect(() => {
    if (status !== "running") return;
    const i = setInterval(() => setHintIdx((v) => (v + 1) % HINTS.length), 2500);
    return () => clearInterval(i);
  }, [status]);

  // Elapsed timer
  useEffect(() => {
    if (status !== "running") return;
    const i = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(i);
  }, [status]);

  const runSync = useCallback(async () => {
    if (!idp) {
      setStatus("error");
      setError("Chýba parameter IDP v URL (napr. ?IDP=8&ROK=2026&MESIAC=6).");
      return;
    }
    try {
      // 1) INIT — quick call: login + ensure store + return month plan
      const initBody = {
        center_id: parseInt(idp, 10),
        rok: parseInt(rok, 10),
        mesiac: parseInt(mesiac, 10),
        force_all: forceAll,
      };
      if (domena) initBody.domena = domena;
      const { data: init } = await api.post("/tido/uvitanie/init", initBody);

      // 2) LOOP months — one HTTP request per month (~2-5s each, well below any timeout).
      // Only months with force=true are fetched. Others are skipped locally (already in DB).
      const monthsToFetch = init.months.filter((m) => m.force);
      const total = init.months.length;
      const fetched = monthsToFetch.length;
      const skipped = total - fetched;

      // Identify the LATEST month with force=true — pass update_store_hp on that one
      // so store's required_hourly_performance is set from the freshest data.
      const latestForceIdx = monthsToFetch.length - 1;

      setProgress({ current: 0, total: monthsToFetch.length, currentMonth: null });

      const perMonthResults = [];
      let inferredHp = null;

      for (let i = 0; i < monthsToFetch.length; i++) {
        const mm = monthsToFetch[i];
        setProgress({ current: i + 1, total: monthsToFetch.length, currentMonth: mm });
        try {
          const { data: mr } = await api.post("/tido/uvitanie/month", {
            domain: init.domain,
            store_id: init.store_id,
            center_id: parseInt(idp, 10),
            year: mm.year,
            month: mm.month,
            update_store_hp: i === latestForceIdx,
          });
          perMonthResults.push(mr);
          if (mr.hourly_performance != null) inferredHp = mr.hourly_performance;
        } catch (e) {
          perMonthResults.push({ year: mm.year, month: mm.month, errors: { fetch: errToString(e) } });
          // Continue with next month — partial success is better than none.
        }
      }

      // 3) DONE
      const summary = {
        store_id: init.store_id,
        store_code: init.store_code,
        store_name: init.store_name,
        months_total: total,
        months_fetched: fetched,
        months_skipped: skipped,
        inferred_hourly_performance: inferredHp,
        per_month: perMonthResults,
      };
      setResult(summary);
      setStatus("done");
      try {
        sessionStorage.setItem("tido_ctx", JSON.stringify({
          idp: String(idp), rok: String(rok), mesiac: String(mesiac),
        }));
        sessionStorage.setItem("tido_locked_store_id", init.store_id);
      } catch { /* ignore */ }
      setLockedStoreId(init.store_id);
      setTimeout(() => {
        navigate(
          `/strediska/${init.store_id}?IDP=${idp}&ROK=${rok}&MESIAC=${mesiac}`,
          { replace: true }
        );
      }, 1500);
    } catch (e) {
      setStatus("error");
      setError(errToString(e));
    }
  }, [idp, rok, mesiac, forceAll, domena, navigate, setLockedStoreId]);

  // Kick off once on mount (StrictMode-safe via ref)
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    runSync();
  }, [runSync]);

  const retry = () => {
    setStatus("running");
    setResult(null);
    setError(null);
    setHintIdx(0);
    setElapsed(0);
    setProgress({ current: 0, total: 0, currentMonth: null });
    runSync();
  };

  const monthName = MESIACE[parseInt(mesiac, 10)] || mesiac;

  return (
    <div className="min-h-screen bg-[var(--bg-page)] flex items-center justify-center px-6" data-testid="tido-uvitanie-page">
      <div className="max-w-xl w-full card-flat p-10 text-center relative">
        {/* Env badge — top-right corner */}
        <div className="absolute top-3 right-3">
          <TidoEnvBadge />
        </div>
        {/* Header */}
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-md bg-[var(--brand)] text-white flex items-center justify-center">
            <Buildings size={26} weight="duotone" />
          </div>
          <div className="text-left">
            <div className="font-display text-lg font-bold tracking-tight">Hodinový výkon & Vyťaženosť</div>
            <div className="label-eyebrow text-[0.6rem]">TiDo Uvítanie</div>
          </div>
        </div>

        {/* Context badge */}
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--brand)]/8 border border-[var(--brand)]/20 text-xs text-[var(--brand)] mb-6 tabular" data-testid="uvitanie-context">
          <strong>IDP:</strong> {idp || "—"} · <strong>ROK:</strong> {rok} · <strong>MESIAC:</strong> {mesiac}{monthName ? ` (${monthName})` : ""}
        </div>

        {status === "running" && (
          <div data-testid="uvitanie-loading">
            <div className="relative w-20 h-20 mx-auto mb-5">
              <div className="absolute inset-0 rounded-full border-4 border-[var(--brand)]/10"></div>
              <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-[var(--brand)] animate-spin"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <CloudArrowDown size={26} weight="duotone" className="text-[var(--brand)]" />
              </div>
            </div>
            <h1 className="font-display text-xl font-bold mb-2">Sťahujem dáta z TiDo…</h1>
            <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-4">
              Naťahujem históriu od <strong>1.1.{parseInt(rok, 10) - 1}</strong> po dnes.
            </p>

            {/* Progress bar */}
            {progress.total > 0 && (
              <div className="mb-4" data-testid="uvitanie-progress">
                <div className="flex justify-between text-[10px] text-[var(--text-muted)] mb-1 tabular">
                  <span>
                    Mesiac <span className="text-[var(--brand)] font-bold">{progress.current}</span> / {progress.total}
                  </span>
                  <span>{Math.round((progress.current / progress.total) * 100)}%</span>
                </div>
                <div className="w-full h-2 bg-[var(--brand)]/10 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[var(--brand)] transition-all duration-300 ease-out"
                    style={{ width: `${(progress.current / progress.total) * 100}%` }}
                  />
                </div>
                {progress.currentMonth && (
                  <div className="text-xs text-[var(--brand)] font-medium mt-2 tabular" data-testid="uvitanie-current-month">
                    {MESIACE[progress.currentMonth.month] || progress.currentMonth.month} {progress.currentMonth.year}
                  </div>
                )}
              </div>
            )}

            <div className="text-xs text-[var(--brand)] font-medium min-h-[1.25rem] transition-opacity" data-testid="uvitanie-hint">
              {progress.total === 0 ? "Prihlasujem sa do TiDo…" : HINTS[hintIdx]}
            </div>
            <div className="mt-4 text-[10px] text-[var(--text-muted)] tabular">
              Uplynulý čas: {Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, "0")}
            </div>
          </div>
        )}

        {status === "done" && result && (
          <div data-testid="uvitanie-done">
            <div className="w-16 h-16 mx-auto rounded-full bg-[var(--success-bg)] flex items-center justify-center mb-4">
              <CheckCircle size={32} weight="fill" className="text-[var(--success)]" />
            </div>
            <h1 className="font-display text-xl font-bold mb-2">Hotovo!</h1>
            <p className="text-sm text-[var(--text-secondary)] mb-4">
              Stredisko <strong>{result.store_name}</strong> ({result.store_code}) je pripravené.
            </p>
            <div className="grid grid-cols-3 gap-3 text-xs mb-4">
              <div className="p-3 rounded bg-[var(--bg-surface-hover)]">
                <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">Stiahnuté</div>
                <div className="font-bold tabular text-lg" data-testid="uvitanie-months-fetched">{result.months_fetched}</div>
                <div className="text-[10px] text-[var(--text-muted)]">mesiacov</div>
              </div>
              <div className="p-3 rounded bg-[var(--bg-surface-hover)]">
                <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">Preskočené</div>
                <div className="font-bold tabular text-lg">{result.months_skipped}</div>
                <div className="text-[10px] text-[var(--text-muted)]">už existujú</div>
              </div>
              <div className="p-3 rounded bg-[var(--bg-surface-hover)]">
                <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">Hodinový cieľ</div>
                <div className="font-bold tabular text-lg">{result.inferred_hourly_performance ?? "—"}</div>
                <div className="text-[10px] text-[var(--text-muted)]">€/os·h</div>
              </div>
            </div>
            <div className="text-xs text-[var(--text-muted)] animate-pulse">Presmerujem na analýzu…</div>
          </div>
        )}

        {status === "error" && (
          <div data-testid="uvitanie-error">
            <div className="w-16 h-16 mx-auto rounded-full bg-[var(--critical-bg)] flex items-center justify-center mb-4">
              <Warning size={32} weight="fill" className="text-[var(--critical)]" />
            </div>
            <h1 className="font-display text-xl font-bold mb-2">Sťahovanie zlyhalo</h1>
            <div className="text-sm bg-[var(--critical-bg)] text-[var(--critical)] p-3 rounded border border-[var(--critical)]/20 text-left mb-4">
              {error}
            </div>
            <button
              onClick={retry}
              className="btn-primary text-sm inline-flex items-center gap-2"
              data-testid="uvitanie-retry-btn"
            >
              <ArrowClockwise size={16} /> Skúsiť znova
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
